/**
 * Variant cover detail — `/inventory/stock/$variantId/cover`.
 *
 * The `InventoryCoverStats` widget with its scope controls plus the server's
 * movement buckets, which state received and issued quantities per age band
 * using the exact boundaries the response publishes.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { useVariantStats, useWarehouseOptions } from "../queries";
import { InventoryCoverStats } from "../components/inventory-cover-stats";
import { QuantityText } from "../components/decimal-text";
import { ALL } from "../components/pickers";

const MIN_DAYS = 1;
const MAX_DAYS = 730;

export interface VariantStatsPageProps {
  variantId?: string;
  initialWarehouseId?: string;
  onOpenStock?: (variantId: string) => void;
  onBack?: () => void;
}

export function VariantStatsPage({
  variantId,
  initialWarehouseId,
  onOpenStock,
  onBack,
}: VariantStatsPageProps) {
  const { t } = useTranslation();
  const [warehouseId, setWarehouseId] = useState<string>(initialWarehouseId ?? ALL);
  const [daysInput, setDaysInput] = useState("90");

  const warehouses = useWarehouseOptions().data ?? [];
  const parsedDays = Number.parseInt(daysInput, 10);
  const daysValid =
    Number.isInteger(parsedDays) && parsedDays >= MIN_DAYS && parsedDays <= MAX_DAYS;

  const stats = useVariantStats(
    variantId,
    {
      ...(warehouseId === ALL ? {} : { warehouse_id: warehouseId }),
      ...(daysValid ? { days: parsedDays } : {}),
    },
    variantId !== undefined,
  );

  if (variantId === undefined) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">
          {t("inventory.cover.noVariant", "Choose a SKU from the stock browser first.")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex max-w-4xl flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("inventory.cover.pageTitle", "Stock cover")}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "inventory.cover.pageSubtitle",
              "How long the current position lasts at the demand the ledger actually recorded.",
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {onOpenStock !== undefined ? (
            <Button type="button" variant="outline" onClick={() => onOpenStock(variantId)}>
              {t("inventory.cover.openStock", "Positions")}
            </Button>
          ) : null}
          {onBack !== undefined ? (
            <Button type="button" variant="ghost" onClick={onBack}>
              {t("inventory.cover.back", "Back")}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cover-warehouse">{t("inventory.common.warehouse", "Warehouse")}</Label>
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger id="cover-warehouse">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>
                {t("inventory.cover.allWarehouses", "Company-wide")}
              </SelectItem>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} — {warehouse.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cover-days">
            {t("inventory.slowMovers.days", "Analysis window (days)")}
          </Label>
          <Input
            id="cover-days"
            dir="ltr"
            inputMode="numeric"
            value={daysInput}
            aria-invalid={!daysValid}
            onChange={(event) => setDaysInput(event.target.value)}
          />
          {daysValid ? null : (
            <p role="alert" className="text-sm text-destructive">
              {t(
                "inventory.slowMovers.daysInvalid",
                "Enter a whole number of days between 1 and 730 — this report cannot run without it.",
              )}
            </p>
          )}
        </div>
      </div>

      <InventoryCoverStats
        variantId={variantId}
        {...(warehouseId === ALL ? {} : { warehouseId })}
        {...(daysValid ? { days: parsedDays } : {})}
      />

      <Card>
        <CardHeader>
          <CardTitle>
            <h2 className="text-base">{t("inventory.cover.buckets", "Movement buckets")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.data === undefined ? (
            <p className="text-sm text-muted-foreground">
              {t("inventory.common.loading", "Loading…")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th scope="col" className="p-2 text-start">
                      {t("inventory.cover.bucket", "Age band")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("inventory.cover.received", "Received")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("inventory.cover.issued", "Issued")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.data.buckets.map((bucket) => {
                    const definition = stats.data?.bucket_definitions.find(
                      (candidate) => candidate.code === bucket.code,
                    );
                    return (
                      <tr key={bucket.code} className="border-b last:border-0">
                        <td className="p-2">
                          <Badge variant="secondary">{bucket.code}</Badge>
                          {definition === undefined ? null : (
                            <span className="ms-2 text-xs text-muted-foreground">
                              {definition.maximum_age_days === null
                                ? t("inventory.reports.bucketOpen", "{{min}}+ days", {
                                    min: definition.minimum_age_days,
                                  })
                                : t("inventory.reports.bucketRange", "{{min}}–{{max}} days", {
                                    min: definition.minimum_age_days,
                                    max: definition.maximum_age_days,
                                  })}
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-end">
                          <QuantityText value={bucket.received_qty} />
                        </td>
                        <td className="p-2 text-end">
                          <QuantityText value={bucket.issued_qty} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
